import { useCallback, useEffect, useMemo, useState } from "react";
import type { EquipmentCategory, EquipmentItem } from "@infoflyt/shared/kameraplan";
import type { Oekt } from "../state/session";
import { useLevende } from "../state/levende";
import * as utstyr from "../state/utstyr";

/**
 * Produksjonens utstyr.
 *
 * A-foto fyller denne når prosjektet settes opp, og endrer den senere — også
 * på settet, gjennom «+ Add new» i kameraplanen. Planene peker hit og lagrer
 * aldri navnet selv: får et utstyr nytt navn, får det nytt navn i alle planer
 * og i huskelista.
 *
 * TALLENE ER VALGFRIE, OG DET ER MENINGEN. Mangler sensorbredden, tegnes
 * ingen synsfeltkjegle — bare en retningspil. Vi gjetter aldri på et tall vi
 * ikke har fått: en kjegle som er tegnet feil ser like riktig ut som en som
 * stemmer.
 *
 * NAVNENE ER ENGELSKE FAGTERMER, som i resten av kameraplanen.
 */

interface Fane {
  readonly slag: EquipmentCategory;
  readonly navn: string;
  readonly plassholder: string;
}

const FANER: readonly Fane[] = [
  { slag: "body", navn: "Cameras", plassholder: "Sony FX6" },
  { slag: "lens", navn: "Lenses", plassholder: "Sony FE 35mm F1.4 GM" },
  { slag: "light", navn: "Lights", plassholder: "ARRI SkyPanel S60" },
  { slag: "grip", navn: "Grip", plassholder: "Matthews 24x36 Flag" },
  { slag: "support", navn: "Support", plassholder: "Dolly track, 3 m" },
];

interface Kolonne {
  readonly key: keyof utstyr.Skriv;
  readonly navn: string;
  readonly bredde: number;
}

/** De viktigste tallene per slag, på én rad. Resten finnes ikke ennå. */
const KOLONNER: Readonly<Record<EquipmentCategory, readonly Kolonne[]>> = {
  body: [{ key: "sensorWidthMm", navn: "Sensor mm", bredde: 92 }],
  lens: [
    { key: "focalLength", navn: "mm", bredde: 64 },
    { key: "zoomMin", navn: "Zoom from", bredde: 86 },
    { key: "zoomMax", navn: "to", bredde: 64 },
  ],
  light: [
    { key: "powerW", navn: "W", bredde: 64 },
    { key: "colorTempMin", navn: "K", bredde: 72 },
    { key: "beamMin", navn: "Beam °", bredde: 78 },
    { key: "beamMax", navn: "to", bredde: 64 },
  ],
  grip: [],
  support: [],
};

/** Raden slik den redigeres: alt som tekst, slik feltene faktisk er. */
type Utkast = Readonly<Record<string, string>>;

function tilUtkast(v: EquipmentItem): Utkast {
  const omraade = (x: number | readonly [number, number] | undefined, i: 0 | 1): string => {
    if (x === undefined) return "";
    if (typeof x === "number") return i === 0 ? String(x) : "";
    return String(x[i]);
  };
  return {
    name: v.name,
    quantityAvailable: v.quantityAvailable === undefined ? "" : String(v.quantityAvailable),
    sensorWidthMm: v.sensorWidthMm === undefined ? "" : String(v.sensorWidthMm),
    focalLength: v.focalLength === undefined ? "" : String(v.focalLength),
    zoomMin: v.zoomRange === undefined ? "" : String(v.zoomRange[0]),
    zoomMax: v.zoomRange === undefined ? "" : String(v.zoomRange[1]),
    powerW: v.powerW === undefined ? "" : String(v.powerW),
    colorTempMin: omraade(v.colorTempK, 0),
    beamMin: omraade(v.beamAngleDeg, 0),
    beamMax: omraade(v.beamAngleDeg, 1),
  };
}

function tilSkriv(slag: EquipmentCategory, u: Utkast): utstyr.Skriv {
  const n = (k: string): number | null => {
    const s = (u[k] ?? "").replace(",", ".").trim();
    if (s === "") return null;
    const v = Number(s);
    return Number.isFinite(v) && v > 0 ? v : null;
  };
  return {
    category: slag,
    name: (u["name"] ?? "").trim(),
    quantityAvailable: n("quantityAvailable"),
    sensorWidthMm: n("sensorWidthMm"),
    focalLength: n("focalLength"),
    zoomMin: n("zoomMin"),
    zoomMax: n("zoomMax"),
    powerW: n("powerW"),
    colorTempMin: n("colorTempMin"),
    colorTempMax: n("colorTempMin"),
    beamMin: n("beamMin"),
    beamMax: n("beamMax"),
  };
}

export function UtstyrScreen({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const produksjonId = oekt.production.id;
  const [alt, setAlt] = useState<readonly EquipmentItem[]>([]);
  const [laster, setLaster] = useState(true);
  const [fane, setFane] = useState<EquipmentCategory>("body");
  const [nytt, setNytt] = useState<Utkast>({});
  const skriver = useMemo(() => ({ paa: false }), []);

  const last = useCallback((): void => {
    void (async () => {
      setAlt(await utstyr.hent(produksjonId));
      setLaster(false);
    })();
  }, [produksjonId]);

  useEffect(last, [last]);
  useLevende("utstyr", ["equipment"], last);

  const mine = useMemo(() => alt.filter((v) => v.category === fane), [alt, fane]);
  const aktive = mine.filter((v) => v.archived !== true);
  const arkiverte = mine.filter((v) => v.archived === true);
  const kolonner = KOLONNER[fane];
  const valgt = FANER.find((f) => f.slag === fane)!;

  const leggTil = useCallback(async (): Promise<void> => {
    const felt = tilSkriv(fane, nytt);
    if (skriver.paa || felt.name === "") return;
    skriver.paa = true;
    setNytt({});
    const v = await utstyr.leggTil(produksjonId, felt);
    skriver.paa = false;
    // Teksten kommer tilbake. Ingenting skal gaa tapt fordi nettet svikter.
    if (v === null) { setNytt(tilUtkast({ ...felt } as EquipmentItem)); return; }
    setAlt((p) => [...p, v]);
  }, [fane, nytt, produksjonId, skriver]);

  const lagre = useCallback(async (v: EquipmentItem, u: Utkast): Promise<void> => {
    const felt = tilSkriv(v.category, u);
    if (felt.name === "") return;
    const foer = alt;
    setAlt((p) => p.map((x) => (x.id === v.id ? { ...x, name: felt.name } : x)));
    if (!await utstyr.endre(v.id, felt)) setAlt(foer);
    else last();
  }, [alt, last]);

  const veksleArkiv = useCallback(async (v: EquipmentItem): Promise<void> => {
    const vil = v.archived !== true;
    setAlt((p) => p.map((x) => (x.id === v.id ? { ...x, archived: vil } : x)));
    if (!await utstyr.arkiver(v.id, vil)) last();
  }, [last]);

  const rad = (v: EquipmentItem): JSX.Element => (
    <Rad
      key={v.id}
      vare={v}
      kolonner={kolonner}
      onLagre={(u) => void lagre(v, u)}
      onArkiv={() => void veksleArkiv(v)}
    />
  );

  return (
    <div className="flate">
      <div className="flate__innhold">
        <div className="utstyr">
          <nav className="utstyr__faner" aria-label="Utstyrsslag">
            {FANER.map((f) => (
              <button
                key={f.slag}
                type="button"
                className={`utstyr__fane${f.slag === fane ? " utstyr__fane--valgt" : ""}`}
                onClick={() => { setFane(f.slag); setNytt({}); }}
                aria-current={f.slag === fane}
              >
                {f.navn}
                <span className="utstyr__antall mono">
                  {alt.filter((v) => v.category === f.slag && v.archived !== true).length}
                </span>
              </button>
            ))}
          </nav>

          {laster ? <p className="synk__melding">Henter utstyret …</p> : (
            <>
              <div className="utstyr__hode">
                <span className="utstyr__kolonne utstyr__kolonne--navn">Name</span>
                {kolonner.map((k) => (
                  <span key={k.key} className="utstyr__kolonne" style={{ width: k.bredde }}>
                    {k.navn}
                  </span>
                ))}
                <span className="utstyr__kolonne" style={{ width: 64 }}>Qty</span>
                <span className="utstyr__kolonne utstyr__kolonne--knapp" />
              </div>

              <ul className="utstyr__liste">
                {aktive.map(rad)}

                <li className="utstyr__rad utstyr__rad--ny">
                  <input
                    className="utstyr__felt utstyr__felt--navn"
                    value={nytt["name"] ?? ""}
                    /* Det konkrete eksempelet bare naar lista er tom. Staar
                       det alt et kamera der, leses «Sony FX6» som en rad til. */
                    placeholder={aktive.length === 0 ? valgt.plassholder : "Legg til"}
                    onChange={(e) => setNytt((p) => ({ ...p, name: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") void leggTil(); }}
                    onBlur={() => void leggTil()}
                    aria-label="Nytt utstyr"
                  />
                  {[...kolonner, { key: "quantityAvailable" as const, navn: "Qty", bredde: 64 }]
                    .map((k) => (
                      <input
                        key={k.key}
                        className="utstyr__felt"
                        style={{ width: k.bredde }}
                        value={nytt[k.key] ?? ""}
                        inputMode="decimal"
                        onChange={(e) => setNytt((p) => ({ ...p, [k.key]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") void leggTil(); }}
                        aria-label={k.navn}
                      />
                    ))}
                  <span className="utstyr__kolonne utstyr__kolonne--knapp" />
                </li>
              </ul>

              {arkiverte.length === 0 ? null : (
                <>
                  {/* Arkivert utstyr er ikke slettet. Planer som bruker det skal
                      fortsatt kunne tegnes, og huskelista skal fortsatt vite. */}
                  <h3 className="utstyr__bolk">Arkivert</h3>
                  <ul className="utstyr__liste utstyr__liste--arkiv">{arkiverte.map(rad)}</ul>
                </>
              )}

              {fane === "body" && aktive.some((v) => v.sensorWidthMm === undefined) ? (
                <p className="merknad">
                  Uten sensorbredde tegnes ingen synsfeltkjegle på kameraet i
                  planen, bare en retningspil. Vi fyller den ikke inn på gjetning.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Rad({
  vare, kolonner, onLagre, onArkiv,
}: {
  readonly vare: EquipmentItem;
  readonly kolonner: readonly Kolonne[];
  onLagre(u: Utkast): void;
  onArkiv(): void;
}): JSX.Element {
  const [u, setU] = useState<Utkast>(() => tilUtkast(vare));
  useEffect(() => { setU(tilUtkast(vare)); }, [vare]);

  const felt = (key: string, bredde?: number, navn?: string): JSX.Element => (
    <input
      key={key}
      className={`utstyr__felt${bredde === undefined ? " utstyr__felt--navn" : ""}`}
      {...(bredde === undefined ? {} : { style: { width: bredde }, inputMode: "decimal" as const })}
      value={u[key] ?? ""}
      onChange={(e) => setU((p) => ({ ...p, [key]: e.target.value }))}
      onBlur={() => onLagre(u)}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      aria-label={navn ?? "Navn"}
    />
  );

  return (
    <li className={`utstyr__rad${vare.archived === true ? " utstyr__rad--arkiv" : ""}`}>
      {felt("name")}
      {kolonner.map((k) => felt(k.key, k.bredde, k.navn))}
      {felt("quantityAvailable", 64, "Qty")}
      <button
        type="button"
        className="utstyr__arkiv"
        onClick={onArkiv}
        title={vare.archived === true ? "Hent fram igjen" : "Arkiver"}
        aria-label={vare.archived === true ? "Hent fram igjen" : "Arkiver"}
      >
        {vare.archived === true ? "↩" : "×"}
      </button>
    </li>
  );
}
